import { ConfigProvider, Select, Tabs, TabsProps } from 'antd';
import React, { useState, useMemo, useEffect } from 'react';
import { Chart } from '@mm/components';
import styles from './SignalGraph.module.css';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export const signalRangeSelectOptions = [
  {
    value: 'week' as const,
    label: 'Last Week',
  },
  {
    value: 'month' as const,
    label: 'Last Month',
  },
  {
    value: '3 months' as const,
    label: 'Last 3 Months',
  },
];

export const SignalGraph = ({
  selectedRangeOption,
  setSelectedRangeOption,
  data,
  activeTab,
  configuration,
}: {
  selectedRangeOption: string;
  setSelectedRangeOption: (
    value: typeof signalRangeSelectOptions[0]['value'],
  ) => void;
  data: {
    labels: string[];
    datasets: {
      label: string;
      data: (number | [number, number])[];
      borderColor: string;
      fill: boolean;
    }[];
  } | null;
  activeTab: 'all' | 'new' | 'existing';
  configuration?: {
    integrations?: string[];
    mode?: string;
  };
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState('1');

  const hasKlaviyo = configuration?.integrations?.includes('klaviyo');
  const isManual = configuration?.mode === 'manual';

  const items: TabsProps['items'] = useMemo(() => {
    const baseItems: TabsProps['items'] = [{ key: '1', label: 'All Events' }];

    if (isManual) {
      baseItems.push({ key: '2', label: 'Website visit' });
    }
    if (hasKlaviyo) {
      baseItems.push({
        key: isManual ? '3' : '2',
        label: 'Active On Site',
      });
    }

    const nextKey = (offset: number) =>
      String(
        parseInt(baseItems[baseItems.length - 1]?.key || '1', 10) + offset,
      );

    const eventItems = [
      'Product View',
      'Collection View',
      'Add to Cart',
      'Checkout Started',
      'Return Visit',
    ].map((label, idx) => ({
      key: nextKey(idx + 1),
      label,
    }));

    baseItems.push(...eventItems);

    if (hasKlaviyo) {
      baseItems.push({
        key: nextKey(eventItems.length + 1),
        label: 'Tracking Gap Klaviyo',
      });
    }

    return baseItems;
  }, [hasKlaviyo, isManual]);

  useEffect(() => {
    const availableKeys = items.map(item => item?.key);
    if (!availableKeys.includes(internalActiveTab)) {
      setInternalActiveTab('1');
    }
  }, [items, internalActiveTab]);

  const { isWhitelabelCompany, primaryColor } = useWhitelabelConfig();

  const tabsColor = isWhitelabelCompany
    ? primaryColor
    : 'var(--color-geekblue-6)';

  const chartData = useMemo(() => {
    const labels = data?.labels ?? [];

    const chartData = {
      labels,
      data: [] as (string | number)[][],
      color: [] as string[],
      title: [] as string[],
      fill: false,
    };

    const selectedLabel = items.find(
      item => item?.key === internalActiveTab,
    )?.label;

    if (selectedLabel && data) {
      const selectedDataset = data.datasets.find(
        d => d.label === selectedLabel,
      );

      if (selectedDataset) {
        const [allValues, resignaledValues] = selectedDataset.data.reduce<
          [number[], number[]]
        >(
          (acc, val) => {
            if (Array.isArray(val)) {
              acc[0].push(val[0]);
              acc[1].push(val[1]);
            } else {
              acc[0].push(val); // fallback in case data is just number
              acc[1].push(0);
            }
            return acc;
          },
          [[], []],
        );

        chartData.data = [allValues, resignaledValues];
        chartData.color = [selectedDataset.borderColor, '#2F54EB'];
        chartData.title = ['All', 'Resignaled'];
      }
    }

    return chartData;
  }, [internalActiveTab, data, items]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>Visitor event data tracking</div>
        <Select
          defaultValue={selectedRangeOption}
          onChange={setSelectedRangeOption}
          options={signalRangeSelectOptions}
          popupMatchSelectWidth={false}
        />
      </div>
      <div className={styles.graphContainer}>
        <ConfigProvider
          theme={{
            components: {
              Tabs: {
                colorPrimary: tabsColor,
                colorPrimaryHover: tabsColor,
              },
            },
          }}
        >
          <Tabs
            items={items}
            onChange={key => setInternalActiveTab(key)}
            activeKey={internalActiveTab}
          />
          <Chart
            className={styles.chart}
            data={chartData}
            type="line"
            additionalOpts={{
              datasetOpts: { pointRadius: 4 },
              borderDashXAxis: [0, 10],
              borderDashYAxis: [10, 0],
              otherOpts: {
                legend: {
                  display: true,
                  align: 'center',
                  position: 'bottom',
                  labels: {
                    usePointStyle: true,
                    boxWidth: 8,
                    padding: 16,
                  },
                },
              },
            }}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};
